<template>
  <div>
    <div class="flex flex-col">
      <div class="bg-light rounded mt-10 py-5 px-5 lg:px-10" v-show="stripeLoaded && hasMethods && readyToPay">
        <h4 class="text-lg font-regular">Express checkout</h4>
        <VueStripeProvider :publishable-key="stripeKey" @load="onStripeLoad">
          <VueStripeElements
              v-if="stripeLoaded"
              :options="elementsOptions"
              @ready="onElementsReady"
              ref="elementsComponent"
          >
            <VueStripeExpressCheckoutElement
                class="mt-4"
                @ready="readyEvent"
                :options="expressCheckoutOptions"
                @confirm="handleConfirm"
                @click="handleClick"
                @cancel="handleCancel"
                ref="expressCheckoutComponent"
            />
          </VueStripeElements>
        </VueStripeProvider>

        <FieldError v-if="error !== ''" :errors="[error]"/>
      </div>

      <div v-if="stripeLoaded && hasMethods && readyToPay">
        <div class="relative flex items-center py-8">
          <div class="flex-grow border-t border-gray-300"></div>
          <span class="flex-shrink mx-4 text-lg text-gray-500 uppercase">Or</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {defineProps, onBeforeMount, ref, useTemplateRef, watch} from "vue"
import {
  VueStripeProvider,
  VueStripeElements,
  VueStripeExpressCheckoutElement
} from '@vue-stripe/vue-stripe'
import useStore from './../store'
import FieldError from "./FieldError.vue";
import {trackBookingComplete} from "../helpers";
import useReadyToPay from "../helpers/useReadyToPay";

const store = useStore();
const error = ref('')
const hasMethods = ref(false)
const stripeInstance = ref(null)
const elementsInstance = ref(null)
const isPaymentSheetShowing = ref(false)

const { readyToPay } = useReadyToPay();

const props = defineProps({
  price: {
    type: [String, Number],
    default: 0,
  },
  config: {
    type: Object,
    default: () => ({})
  },
  preCheckoutValidator: {
    type: Function,
    default: null
  }
});


/**
 * DEFERRED PAYMENT
 * This Payment Element example is based on
 * https://docs.stripe.com/payments/accept-a-payment-deferred?type=payment
 */

// const stripeKey = "pk_test_OKDoqGqz6yprOyyhtChnHXVq"
const stripeKey = props.config.token;

const elementsOptions = ref({
  // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
  mode: "payment",
  amount: 2500,
  currency: "aud",
  loader: 'always',
})

const expressCheckoutOptions = ref({
  paymentMethods: {
    applePay: 'auto',  // auto | never | always
    googlePay: 'auto', // auto | never | always
    link: 'never', // auto | never
  },
  paymentMethodOrder: [
    'apple_pay',
    'google_pay',
    // 'link'
  ],
  emailRequired: true,
  phoneNumberRequired: true,
  billingAddressRequired: true
  // https://docs.stripe.com/js/elements_object/create_express_checkout_element#express_checkout_element_create-options
})

const paymentElementOptions = ref({
  paymentMethodOrder: [
    'card',
    'apple_pay',
    'google_pay',
    'link'
  ],
  layout: {
    type: 'tabs',
  }
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options
})
const stripeLoaded = ref(false)
const elementsComponent = useTemplateRef("elementsComponent")
const expressCheckoutComponent = useTemplateRef("expressCheckoutComponent")

const emit = defineEmits([
    'errors',
    'setPage',
    'top',
    'paymentConfirmed'
]);

const onStripeLoad = (stripe) => {
  stripeInstance.value = stripe
  stripeLoaded.value = true
}

const onElementsReady = (elements) => {
  elementsInstance.value = elements
}

const readyEvent = (event) => {
  if (event.availablePaymentMethods){
    for (const [key, value] of Object.entries(event.availablePaymentMethods)) {
      if (value === true) {
        hasMethods.value = true;
      }
    }
  }

  if (event.error) {
    error.value = event.error.message;
  } else {
    error.value = null;
  }
}

const handleClick = async (event) => {

  if (!isPaymentSheetShowing.value && elementsInstance.value?.update) {
    elementsInstance.value.update({amount: store.totalDueCents });
  }

  let isPreCheckoutValid = true;
  if (props.preCheckoutValidator) {
    isPreCheckoutValid = await props.preCheckoutValidator();
  }

  if (readyToPay.value && isPreCheckoutValid) {
    isPaymentSheetShowing.value = true;
    // If everything is fine, call event.resolve() within 1 second
    event.resolve();
  } else {
    isPaymentSheetShowing.value = false;
    // If there's an issue, call event.reject() within 1 second
    event.reject();
    // Optionally display an error message to the user
    if (!isPreCheckoutValid) {
      return;
    }
    emit('errors', ['Validation failed. Cannot proceed with payment.'])
  }
}

const handleConfirm = async (event) => {

  if (!stripeInstance.value || !elementsInstance.value) {
    isPaymentSheetShowing.value = false;
    emit('errors', ['Please wait for the payment form to finish loading.']);
    return false;
  }

  const { error, confirmationToken } = await stripeInstance.value.createConfirmationToken({
    // elements: event.elements,
    elements: elementsInstance.value,
    params: {
      return_url: store.options.confirm_url,
    }
  })

  if (error) {
    console.error(error.message)
    emit('errors', [error.message])
    return;
  }

  store.setPayment({
    confirmation: confirmationToken
  })

  //return true;
  console.log('confirmationToken', confirmationToken)

  emit('paymentConfirmed')
  isPaymentSheetShowing.value = false;
}

const handleCancel = () => {
  isPaymentSheetShowing.value = false;
}

// Stripe loading is now handled by VueStripeProvider via @load event

watch(
    () => store.totalDueCents,
    () => {
      console.log('express quote change', store.totalDueCents)
      if (store.totalDueCents){
        // prepareIntent()
        prepareElements()
      }
    },
    { deep: true }
)


const prepareElements = async () => {
  elementsOptions.value = {
    mode: "payment",
    amount: store.totalDueCents,
    currency: "aud",
  }
  if (!isPaymentSheetShowing.value && elementsInstance.value?.update) {
    elementsInstance.value.update({amount: store.totalDueCents });
  }
}

const prepareIntent = async () => {
  return await store.preparePayment();
};


const handleError = (details) => {
  console.error(details)
  error.value = details.message;
}


const handleServerResponse = async (data) => {
  console.log('handleServerResponse', data)

  if (data.error) {
    // Show error from server on payment form
    console.error(data.error)
    emit('errors', [data.error.message])
  } else if (data?.intent?.status === "requires_action") {
    // Use Stripe.js to handle the required next action
    const {
      error,
      paymentIntent
    } = await stripeInstance.value.handleNextAction({
      clientSecret: data?.intent?.client_secret
    });

    if (error) {
      // Show error from Stripe.js in payment form
      console.error(error)

      emit('errors', [error.message])
    } else {
      // Actions handled, show success message
      console.log('paymentIntent', paymentIntent)
      store.setBooking(data.booking);
      store.setBookingToken(data?.retailBooking?.token);
      store.setStatus("success");
      trackBookingComplete(data.booking)
      emit('setPage', 'thanks');
      emit('top')
    }
  } else {
    // No actions needed, show success message

    store.setBooking(data.booking);
    store.setBookingToken(data?.retailBooking?.token);
    store.setStatus("success");
    trackBookingComplete(data.booking)
    emit('setPage', 'thanks');
    emit('top')
  }
}

defineExpose({
  handleServerResponse
})

</script>
